// Submissions — /dashboard/submissions
//
// Lists location task submissions with filtering by status / site /
// officer / task / date window. Click a row to open detail drawer (or
// navigate to /dashboard/submissions/:id).
//
// Backend: GET /v1/admin/submissions (PR N).
// Spec: docs/specs/location-tasks.md §11.1.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, RefreshCw, Filter, ServerCrash, Inbox } from "lucide-react";
import PageHeader from "../components/PageHeader.js";
import DataTable, { type Column } from "../components/DataTable.js";
import EmptyState from "../components/EmptyState.js";
import Skeleton from "../components/Skeleton.js";
import PaginationControl from "../components/PaginationControl.js";
import { usePagination } from "../lib/usePagination.js";
import {
  listSubmissions, statusLabel, statusTone,
  type SubmissionListItem, type ListFilters,
} from "../lib/submissionsApi.js";
import { listOfficers, type OfficerRow } from "../lib/usersApi.js";
import { listSites, type SiteRow } from "../lib/sitesApi.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import { formatTaipeiDateTime } from "../../lib/time.js";

type ListState =
  | { status: "loading" }
  | { status: "ok"; items: SubmissionListItem[]; hasMore: boolean }
  | { status: "error"; message: string };

const STATUSES: { value: string; label: string }[] = [
  { value: "", label: "全部狀態" },
  { value: "in_progress", label: "進行中" },
  { value: "submitted", label: "已提交" },
  { value: "reviewed", label: "已審核" },
  { value: "skipped", label: "已跳過" },
  { value: "expired", label: "已過期" },
];

export default function Submissions(): React.ReactElement {
  const session = useDashboardSession();
  const tenantId = session.super_admin ? session.acting_tenant_id ?? null : null;
  const [state, setState] = useState<ListState>({ status: "loading" });
  // 預設不卡 status — operators 報過「我建了 2 個任務派發 + 巡邏員 tap
  // 完 → 列表只看到 1 筆」的 confusion，原因是預設只顯示 `submitted` 把
  // 還沒完成 wizard 的 `in_progress` 隱掉了。改成預設「全部狀態」，user
  // 想專注某個 status 自己從下拉選即可。
  // Non-pagination filters (status / site / officer / task / date window).
  // limit/offset live on the usePagination hook; months stays here for
  // legacy「過去 3 個月」 controls.
  const [filters, setFilters] = useState<Omit<ListFilters, "limit" | "offset">>({ months: 3 });
  const pag = usePagination({ urlPrefix: "sub_" });
  // Officer / site dropdown options. Loaded once per tenant context (reload
  // when super_admin switches acting tenant). Plain text inputs for IDs
  // were unusable for non-engineers — operators don't know the ULID and
  // shouldn't have to copy-paste hex strings to filter.
  const [officers, setOfficers] = useState<OfficerRow[]>([]);
  const [sites, setSites] = useState<SiteRow[]>([]);

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const r = await listSubmissions({ ...filters, limit: pag.limit, offset: pag.offset });
      setState({ status: "ok", items: r.items, hasMore: r.has_more });
    } catch (e) {
      setState({ status: "error", message: (e as Error).message });
    }
  }, [filters, pag.limit, pag.offset]);

  useEffect(() => { void load(); }, [load]);

  // Refresh dropdown options whenever the acting tenant changes (super_admin
  // flow). Failures silently downgrade to an empty list — the dropdown
  // becomes "全部官員 / 全部案場" only, no error toast, since the page
  // is still usable.
  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      listOfficers({ tenantId, status: "active" }).catch(() => ({ items: [] })),
      listSites({ tenantId, status: "active", limit: 200 }).catch(() => ({ items: [], total: 0, correlation_id: null })),
    ]).then(([o, s]) => {
      if (cancelled) return;
      setOfficers(o.items);
      setSites(s.items);
    });
    return () => { cancelled = true; };
  }, [tenantId]);

  const cols = useMemo<Column<SubmissionListItem>[]>(() => [
    {
      key: "submitted_at", header: "提交時間",
      render: (r) => (
        <span className="text-xs">
          {r.submitted_at ? formatTaipeiDateTime(r.submitted_at)
            : <span className="text-text-muted">— 進行中</span>}
        </span>
      ),
    },
    {
      key: "task_name", header: "任務", primary: true,
      render: (r) => (
        <Link to={`/dashboard/submissions/${r.id}`} className="text-sm font-medium hover:text-accent">
          {r.task_name ?? r.task_id.slice(0, 10)}
        </Link>
      ),
    },
    {
      key: "location_label", header: "地點",
      render: (r) => (
        <span className="text-xs text-text-secondary">
          {r.location_label ?? r.location_id?.slice(0, 8) ?? "—"}
        </span>
      ),
    },
    {
      key: "officer", header: "巡邏員",
      render: (r) => (
        <span className="text-xs">{r.officer_display_name ?? r.officer_id.slice(0, 10)}</span>
      ),
    },
    {
      key: "status", header: "狀態",
      render: (r) => <Pill tone={statusTone(r.status)}>{statusLabel(r.status)}</Pill>,
    },
  ], []);

  return (
    <div className="space-y-4">
      <PageHeader
        title="任務提交"
        description="檢視巡邏員提交的任務 + 各階段驗證 / 證據 / 簽名 + 回覆紀錄"
        actions={
          <button onClick={() => void load()} className="btn-secondary inline-flex items-center gap-1">
            <RefreshCw className="h-4 w-4" /> 重新整理
          </button>
        }
      />

      <section className="card p-4">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1 text-text-muted">
            <Filter className="h-3.5 w-3.5" /> 篩選：
          </span>
          <select
            value={filters.status ?? ""}
            onChange={(e) => pag.withFilter(setFilters)((f) => ({ ...f, status: e.target.value || undefined }))}
            className="rounded-md bg-bg-raised border border-border px-2 py-1 text-xs"
          >
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select
            value={filters.officer_id ?? ""}
            onChange={(e) => pag.withFilter(setFilters)((f) => ({ ...f, officer_id: e.target.value || undefined }))}
            className="rounded-md bg-bg-raised border border-border px-2 py-1 text-xs"
          >
            <option value="">全部巡邏員</option>
            {officers.map((o) => (
              <option key={o.id} value={o.id}>{o.display_name}</option>
            ))}
          </select>
          <select
            value={filters.site_id ?? ""}
            onChange={(e) => pag.withFilter(setFilters)((f) => ({ ...f, site_id: e.target.value || undefined }))}
            className="rounded-md bg-bg-raised border border-border px-2 py-1 text-xs"
          >
            <option value="">全部案場</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <select
            value={filters.months ?? 3}
            onChange={(e) => pag.withFilter(setFilters)((f) => ({ ...f, months: Number(e.target.value) }))}
            className="rounded-md bg-bg-raised border border-border px-2 py-1 text-xs"
          >
            {[1, 3, 6, 12].map((m) => <option key={m} value={m}>近 {m} 個月</option>)}
          </select>
        </div>
      </section>

      {state.status === "loading" ? (
        <Skeleton className="h-48" />
      ) : state.status === "error" ? (
        <EmptyState
          icon={ServerCrash}
          title="載入失敗"
          description={state.message}
          action={<button onClick={() => void load()} className="btn-primary">重試</button>}
        />
      ) : state.items.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="目前條件下沒有任務提交"
          description="調整篩選條件或新增任務指派後再回來。"
        />
      ) : (
        <>
          <DataTable
            rows={state.items}
            columns={cols}
            rowKey={(r) => r.id}
            searchableText={(r) => `${r.task_name ?? ""} ${r.location_label ?? ""} ${r.officer_display_name ?? ""}`}
            serverPagination
          />
          <div className="card mt-3">
            <PaginationControl
              page={pag.page}
              pageSize={pag.limit}
              total={null}
              hasMore={state.hasMore}
              onPageChange={pag.setPage}
              onPageSizeChange={pag.setLimit}
            />
          </div>
        </>
      )}
    </div>
  );
}

function Pill({ tone, children }: { tone: "default" | "success" | "warning" | "danger" | "info"; children: React.ReactNode }): React.ReactElement {
  const cls =
    tone === "success" ? "bg-success/15 text-success" :
    tone === "warning" ? "bg-warning/15 text-warning" :
    tone === "danger"  ? "bg-danger/15 text-danger" :
    tone === "info"    ? "bg-accent/15 text-accent" :
                         "bg-bg-raised text-text-secondary";
  return <span className={`inline-block text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${cls}`}>{children}</span>;
}
